/* ==========================================================================
   Admin Billing Page
   Task F35: Subscription management, usage meter, and Stripe portal link
   ========================================================================== */

import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { usePortalSlug } from '@/context/PortalContext'
import { useToast } from '@/context/ToastContext'
import {
  getSubscription,
  createPortalSession,
  type SubscriptionDetail,
} from '@/api/admin'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
}

const CYCLE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  canceled: 'bg-red-100 text-red-800',
  incomplete: 'bg-gray-100 text-gray-600',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  past_due: 'Past Due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const CURRENCY_SYMBOLS: Record<string, string> = { usd: '$', cad: 'CA$' }

function formatCurrency(cents: number, currency: string): string {
  return `${CURRENCY_SYMBOLS[currency] ?? '$'}${(cents / 100).toFixed(2)}`
}

function getUsageColor(ratio: number): string {
  if (ratio >= 1) return 'bg-red-500'
  if (ratio >= 0.8) return 'bg-orange-500'
  if (ratio >= 0.5) return 'bg-yellow-500'
  return 'bg-green-500'
}

/**
 * Name the plan a deferred downgrade will switch to.
 *
 * The billing frequency is deferred alongside the tier, so the cycle is named
 * whenever it is actually changing — otherwise a customer moving off an annual
 * plan reads "changing to Starter" and still expects one annual invoice.
 */
function formatPendingPlan(s: SubscriptionDetail): string {
  const tier = s.pending_tier ?? s.tier
  const cycle = s.pending_billing_cycle ?? s.billing_cycle
  const tierLabel = TIER_LABELS[tier] ?? tier
  const cycleLabel = CYCLE_LABELS[cycle] ?? cycle

  return s.pending_billing_cycle && s.pending_billing_cycle !== s.billing_cycle
    ? `${tierLabel} (${cycleLabel} billing)`
    : tierLabel
}

const OVERAGE_RATE_CENTS = 30

/* --------------------------------------------------------------------------
   Loading Skeleton
   -------------------------------------------------------------------------- */

function BillingSkeleton() {
  return (
    <div className="p-6 sm:p-8 max-w-4xl animate-pulse">
      <div className="h-8 w-48 bg-gray-200 rounded mb-6" />
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
            <div className="h-4 w-1/2 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-4 w-full bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Billing Page
   -------------------------------------------------------------------------- */

export function Billing() {
  const slug = usePortalSlug()
  const { error: showError } = useToast()
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    async function fetch() {
      try {
        const data = await getSubscription(slug)
        setSubscription(data)
      } catch {
        // 404 means no subscription — show empty state
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [slug])

  const handleManageBilling = async () => {
    setPortalLoading(true)
    try {
      const result = await createPortalSession(slug)
      window.open(result.portal_url, '_blank')
    } catch {
      showError('Failed to open billing portal. Please try again.')
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) return <BillingSkeleton />

  if (!subscription) {
    return (
      <div className="p-6 sm:p-8 max-w-4xl">
        <h1 className="text-2xl font-serif text-charcoal mb-6">Billing</h1>
        <Card>
          <p className="text-charcoal/70">
            No subscription found. Please contact support if you believe this is
            an error.
          </p>
        </Card>
      </div>
    )
  }

  const usageRatio = subscription.script_limit > 0
    ? subscription.script_count / subscription.script_limit
    : 0
  const usagePercent = Math.min(usageRatio * 100, 100)

  return (
    <div className="p-6 sm:p-8 max-w-4xl">
      <h1 className="text-2xl font-serif text-charcoal mb-6">Billing</h1>

      <div className="space-y-6">
        {/* Subscription Card */}
        <Card header="Subscription">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-charcoal/60">Plan</p>
              <p className="text-lg font-semibold text-charcoal">
                {TIER_LABELS[subscription.tier] ?? subscription.tier}
              </p>
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Status</p>
              <span
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  STATUS_STYLES[subscription.status] ?? 'bg-gray-100 text-gray-600'
                }`}
              >
                {STATUS_LABELS[subscription.status] ?? subscription.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Billing Cycle</p>
              <p className="text-charcoal">
                {subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
              </p>
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Current Period</p>
              <p className="text-charcoal">
                {formatDate(subscription.current_period_start)} &ndash;{' '}
                {formatDate(subscription.current_period_end)}
              </p>
            </div>
          </div>
        </Card>

        {/* Usage Meter */}
        <Card header="Usage">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-charcoal/60">Scripts used</span>
              <span className="font-medium text-charcoal">
                {subscription.script_count.toLocaleString()} /{' '}
                {subscription.script_limit.toLocaleString()}
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${getUsageColor(usageRatio)}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>

            <p className="text-xs text-charcoal/50">
              {Math.round(usagePercent)}% of your plan limit used
            </p>

            {subscription.overage_count > 0 && (
              <div className="mt-3 p-3 bg-red-50 rounded-md border border-red-200">
                <p className="text-sm text-red-800">
                  <span className="font-medium">
                    {subscription.overage_count.toLocaleString()} overage scripts
                  </span>{' '}
                  &mdash; estimated cost:{' '}
                  {formatCurrency(
                    subscription.overage_count * OVERAGE_RATE_CENTS,
                    subscription.currency
                  )}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Price & Pending Downgrade */}
        <Card header="Plan Pricing">
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-charcoal/60">Current price</span>
              <span className="text-xl font-semibold text-charcoal">
                {formatCurrency(
                  subscription.price_amount_cents,
                  subscription.currency
                )}
                <span className="text-sm font-normal text-charcoal/50">
                  /{subscription.billing_cycle === 'annual' ? 'yr' : 'mo'}
                </span>
              </span>
            </div>

            {subscription.next_invoice_date && (
              <div className="flex justify-between text-sm">
                <span className="text-charcoal/60">Next invoice</span>
                <span className="text-charcoal">
                  {formatDate(subscription.next_invoice_date)}
                </span>
              </div>
            )}

            {(subscription.pending_tier ||
              subscription.pending_billing_cycle) && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  Your plan will change to{' '}
                  <span className="font-medium">
                    {formatPendingPlan(subscription)}
                  </span>{' '}
                  at the start of your next billing period.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Manage Billing Button */}
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-charcoal font-medium">Manage Billing</p>
              <p className="text-sm text-charcoal/60">
                Update payment method, view invoices, or change your plan.
              </p>
            </div>
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-deep-purple text-white rounded-md font-medium text-sm hover:bg-deep-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-lavender focus:ring-offset-2"
            >
              {portalLoading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Opening...
                </>
              ) : (
                <>
                  Manage Billing
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Billing
